import {BasisType, Goal, Partner, RRObject, Service} from "@raskrask-dk/raskrask-utils";
import type { IBookableSchedulesDTO} from "..";
import {Mapper} from "../mapper";
import type {MapById} from "../mapper/mapper.types";
import {emptyDomainSchedules, emptyDTOSchedules, IBookableAssembler, ISchedules} from "./bookable.interface";

const isT = <T>(item: T | undefined): item is T => {
  return !!item;
};

export class BookableAssembler implements IBookableAssembler {
  private dtoStore: Map<BasisType["id"], IBookableSchedulesDTO> = new Map(); // accumulative dtos, merged
  private domainStore: ISchedules = emptyDomainSchedules;

  constructor(public mapper: Mapper = new Mapper()) {}

  createSchedules(dtos: IBookableSchedulesDTO, basisType: BasisType): ISchedules {
    this.dtoStore.clear();
    this.domainStore = emptyDomainSchedules;
    this.dtoStore.set(basisType.id, dtos);
    const {goals, services, partners} = this.mapper.fromNormalized(dtos, basisType);
    const schedules = this.combineAggregates(goals, services, partners, dtos);
    this.domainStore = schedules;
    return schedules;
  }

  addSchedules(dtos: IBookableSchedulesDTO, basisType: BasisType): ISchedules {
    const currentValue = this.dtoStore.get(basisType.id) ?? emptyDTOSchedules;
    const merged = this.merge(currentValue, dtos);
    this.dtoStore.set(basisType.id, merged);
    const {goals, services, partners} = this.mapper.fromNormalized(merged, basisType);
    const schedules = this.combineAggregates(goals, services, partners, merged);
    this.domainStore = schedules;

    return schedules;
  }

  public schedules(): ISchedules {
    return {...this.domainStore};
  }

  private addServicesToGoals(dto: IBookableSchedulesDTO, allGoals: MapById<Goal>, allServices: MapById<Service>): void {
    for (const goalId of dto.goals.allIds) {
      const goalDTO = dto.goals.byId[goalId];
      const goal = allGoals.get(goalDTO.id);
      if (!goal) throw new Error(`Goal with id ${goalDTO} not found`);
      const services = goalDTO.serviceIds.map((serviceId) => allServices.get(serviceId)).filter(isT);
      goal.addServiceTypes(services);
    }
  }

  private addPartnersToServices(dto: IBookableSchedulesDTO, allServices: MapById<Service>, allPartners: MapById<Partner>): void {
    allPartners.forEach((partner) => {
      const offerDTO = dto.partners.byId[partner.id].offers;
      offerDTO.forEach((offer) => {
        const {serviceId, price} = offer;
        const service = allServices.get(serviceId);
        if (service) service.addPartner(partner, price);
      });
    });
  }

  private combineAggregates(goals: MapById<Goal>, services: MapById<Service>, partners: MapById<Partner>, merged: IBookableSchedulesDTO): ISchedules {
    this.addServicesToGoals(merged, goals, services);
    this.addPartnersToServices(merged, services, partners);
    return this.mapper.fromMaps({goals, services, partners});
  }

  private merge(dtos1: IBookableSchedulesDTO, dtos2: IBookableSchedulesDTO): IBookableSchedulesDTO {
    const dtosResult = RRObject.mergeObjects({...dtos1}, {...dtos2}) as unknown as IBookableSchedulesDTO;
    dtosResult.goals.allIds = [...new Set([...dtos1.goals.allIds, ...dtos2.goals.allIds])];
    dtosResult.services.allIds = [...new Set([...dtos1.services.allIds, ...dtos2.services.allIds])];
    dtosResult.partners.allIds = [...new Set([...dtos1.partners.allIds, ...dtos2.partners.allIds])];
    dtosResult.partners.allIds.forEach((id) => {
      // remove duplicate
      dtosResult.partners.byId[id].schedules = dtosResult.partners.byId[id].schedules.filter((s1, i, all) => {
        return (
          all.findIndex((s2) => {
            return s1.to === s2.to && s1.from === s2.from;
          }) === i
        );
      });
      if (dtos1.partners.byId[id] !== undefined) {
        dtosResult.partners.byId[id].tools = dtos1.partners.byId[id].tools;
        dtosResult.partners.byId[id].reviews = dtos1.partners.byId[id].reviews;
        dtosResult.partners.byId[id].profileTexts = dtos1.partners.byId[id].profileTexts;
      }
    });

    return dtosResult;
  }
}
